
import { Component, Prop } from 'vue-property-decorator';
import VFormBuilder from '../../shared/form/VFormBuilder.vue';
import { InputType } from '../../../enums/InputType';
import StackedForm from '../../../mixins/StackedForm';
import { mixins } from 'vue-class-component';
import { convertEmptyToNull } from '@/util/helper';
import { OrderOffset } from '@/interfaces/models/OrderOffset';
import { CustomerGroup } from '@/enums/CustomerGroup';

interface OffsetData {
  standardOffsets?: OrderOffset;
  preorderOffsets: {
    inside: OrderOffset;
    takeAway: OrderOffset;
    delivery: OrderOffset;
  };
  terminalOffsets: {
    inside: OrderOffset;
    takeAway: OrderOffset;
  };
}

@Component({
  components: { VFormBuilder },
})
export default class OrderOffsetForm extends mixins(StackedForm) {
  @Prop({ type: Boolean, default: false }) public disableTerminal!: boolean;
  @Prop({ type: Boolean, default: false }) public disableStandard!: boolean;
  @Prop({ type: Boolean, default: false }) public disablePreorderInside!: boolean;
  @Prop({ type: Boolean, default: false }) public disablePreorderDelivery!: boolean;
  @Prop({ type: Boolean, default: false }) public disablePreorderTakeAway!: boolean;
  @Prop({ type: String, default: '' }) public customerGroup!: CustomerGroup;
  @Prop({ type: Boolean, default: false }) public readonly editing!: boolean;

  get standardInitialValues() {
    // @ts-ignore
    if (this.initialValues && this.initialValues.offsets && this.initialValues.offsets.standard) {
      // @ts-ignore
      return this.initialValues.offsets.standard;
    }

    return {};
  }

  get offsetResetInitialValues() {
    const initVals = this.initialValues as any;

    if (!initVals?.offsetReset) {
      return {};
    }

    return initVals.offsetReset;
  }

  get offsets() {
    return [
      { name: 'first', label: 'venue.form.firstOrderOffset', type: InputType.Text, default: 0 },
      { name: 'offset', label: 'venue.form.orderOffset', type: InputType.Text, default: 0 },
      { name: 'last', label: 'venue.form.lastOrderOffset', type: InputType.Text, default: 0 },
    ];
  }

  get preorderTakeAwayOffsets() {
    return [
      ...this.offsets,
      {
        name: 'defaultPreparationTime',
        type: InputType.Text,
        label: 'venue.form.externalOrderProviders.defaultPreparationTime',
        hint: 'venue.form.externalOrderProviders.defaultPreparationTimeHint',
        rules: 'integer|min_value:0|smaller_eq_than:60',
      },
    ];
  }

  get snoozing() {
    return [
      {
        name: 'dailyUnsnoozeTime',
        label: 'venue.form.dailyUnsnoozeTime',
        type: InputType.Text,
        rules: 'time',
        clearable: true,
      },
    ];
  }

  get offsetReset() {
    return [
      {
        name: 'enabled',
        label: 'venue.form.offsetResetEnabled',
        type: InputType.Checkbox,
        default: false,
      },
      {
        name: 'time',
        label: 'venue.form.offsetResetTime',
        type: InputType.DateTime,
        rules: 'required',
        visible: (form: { enabled: boolean }) => !!form.enabled,
        allowedMinutes: (m: number) => m % 15 === 0,
        onlyTime: true,
      },
    ];
  }

  public reset() {
    if (!this.disableStandard) {
      this.$refs.standard.reset();
    }
    if (!this.disableTerminal) {
      this.$refs.terminalInside.reset();
      this.$refs.terminalTakeAway.reset();
    }

    if (this.$refs.preorderInside) {
      this.$refs.preorderInside.reset();
    }
    if (this.$refs.preorderTakeAway) {
      this.$refs.preorderTakeAway.reset();
    }
    if (this.$refs.preorderDelivery) {
      this.$refs.preorderDelivery.reset();
    }
  }

  public getData() {
    const data: any = {
      preorder: {
        inside: { ...convertEmptyToNull(this.$refs.preorderInside ? this.$refs.preorderInside.form : null) },
        takeAway: { ...convertEmptyToNull(this.$refs.preorderTakeAway ? this.$refs.preorderTakeAway.form : null) },
        delivery: { ...convertEmptyToNull(this.$refs.preorderDelivery ? this.$refs.preorderDelivery.form : null) },
      },
    };
    if (!this.disableStandard) {
      data.standard = { ...convertEmptyToNull(this.$refs.standard.form) };
    }
    if (!this.disableTerminal) {
      data.terminal = {
        inside: { ...convertEmptyToNull(this.$refs.terminalInside.form) },
        takeAway: { ...convertEmptyToNull(this.$refs.terminalTakeAway.form) },
      };
    }

    data.catering = {
      takeAway: { ...convertEmptyToNull(this.$refs.cateringTakeAway ? this.$refs.cateringTakeAway.form : null) },
      delivery: { ...convertEmptyToNull(this.$refs.cateringDelivery ? this.$refs.cateringDelivery.form : null) },
    };

    return { offsets: data };
  }

  public getTerminalInitialValues(type: 'inside' | 'takeAway') {
    if (
      this.initialValues &&
      // @ts-ignore
      this.initialValues.offsets &&
      // @ts-ignore
      this.initialValues.offsets.terminal &&
      // @ts-ignore
      this.initialValues.offsets.terminal[type]
    ) {
      // @ts-ignore
      return this.initialValues.offsets.terminal[type];
    }

    return {};
  }

  public getPreorderInitialValues(type: 'inside' | 'delivery' | 'takeAway') {
    if (
      this.initialValues &&
      // @ts-ignore
      this.initialValues.offsets &&
      // @ts-ignore
      this.initialValues.offsets.preorder &&
      // @ts-ignore
      this.initialValues.offsets.preorder[type]
    ) {
      // @ts-ignore
      return this.initialValues.offsets.preorder[type];
    }

    return {};
  }

  public getCateringInitialValues(type: 'delivery' | 'takeAway') {
    if (
      this.initialValues &&
      // @ts-ignore
      this.initialValues.offsets &&
      // @ts-ignore
      this.initialValues.offsets.catering &&
      // @ts-ignore
      this.initialValues.offsets.catering[type]
    ) {
      // @ts-ignore
      return this.initialValues.offsets.catering[type];
    }

    return {};
  }

  public getDailyUnsnoozeTime() {
    const dailyUnsnoozeTime = this.$refs?.snoozing?.form?.dailyUnsnoozeTime;

    if (dailyUnsnoozeTime) {
      return { dailyUnsnoozeTime };
    }

    if (this.initialValues?.hasOwnProperty('dailyUnsnoozeTime')) {
      return { dailyUnsnoozeTime: null };
    }

    return {};
  }

  public getOffsetReset() {
    const form = this.$refs?.offsetReset?.form;
    const offsetResetEnabled = form?.enabled;
    const offsetResetTime = form?.time;

    if (offsetResetEnabled) {
      return {
        offsetReset: {
          enabled: offsetResetEnabled,
          time: offsetResetTime,
        },
      };
    }

    return { offsetReset: null };
  }
}
